import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle, Mail, Building, Calendar, Clock, Eye, MessageSquare, Edit } from "lucide-react";
import { format } from "date-fns";

interface InvitationData {
  id: string;
  email: string;
  name?: string;
  accessLevel: string;
  status: string;
  expiresAt: string;
  message?: string;
  project: {
    id: string;
    title?: string;
    address?: string;
    status?: string;
  };
}

const accessLevelConfig = {
  view: { icon: Eye, label: "View Only", description: "You can view project details and documents" },
  comment: { icon: MessageSquare, label: "View & Comment", description: "You can view and add comments" },
  edit: { icon: Edit, label: "Full Access", description: "You can view, comment, and make changes" },
};

export default function AcceptGuestInvitation() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const [guestEmail, setGuestEmail] = useState("");
  const [accepted, setAccepted] = useState(false);

  const { data: invitation, isLoading, error } = useQuery<{ success: boolean; data: InvitationData }>({
    queryKey: ['/api/v1/guest/invitation', token],
    queryFn: async () => {
      const response = await fetch(`/api/v1/guest/invitation/${token}`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Invitation not found');
      }
      return response.json();
    },
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/v1/guest/invitation/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestEmail }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to accept invitation');
      }
      return response.json();
    },
    onSuccess: () => {
      setAccepted(true);
      localStorage.setItem('guestAccessToken', token || '');
    },
  });

  const handleAccept = () => {
    if (guestEmail.toLowerCase() === invitation?.data?.email?.toLowerCase() || !invitation?.data?.email) {
      acceptMutation.mutate();
    }
  };

  const handleAccessProject = () => {
    setLocation(`/guest/project/${token}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !invitation?.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold mb-2">Invitation Unavailable</h2>
            <p className="text-muted-foreground">
              {(error as Error)?.message || "This invitation link is no longer valid."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const inviteData = invitation.data;
  const accessConfig = accessLevelConfig[inviteData.accessLevel as keyof typeof accessLevelConfig] || accessLevelConfig.view;
  const AccessIcon = accessConfig.icon;

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-semibold mb-2">Invitation Accepted!</h2>
            <p className="text-muted-foreground mb-6">
              You now have access to the project.
            </p>
            <Button 
              onClick={handleAccessProject} 
              className="w-full"
              data-testid="access-project-button"
            >
              Access Project
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Project Invitation</CardTitle>
          <CardDescription>
            You've been invited to collaborate on a project
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-primary/5 rounded-lg p-4 space-y-3">
            <div>
              <h3 className="font-semibold text-lg">
                {inviteData.project.title || "Untitled Project"}
              </h3>
              {inviteData.project.address && (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                  <Building className="w-4 h-4" />
                  {inviteData.project.address}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm">
              <AccessIcon className="w-4 h-4 text-primary" />
              <span className="font-medium">{accessConfig.label}</span>
              <span className="text-muted-foreground">- {accessConfig.description}</span>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>
                Access expires: {format(new Date(inviteData.expiresAt), "MMMM d, yyyy")}
              </span>
            </div>
          </div>

          {inviteData.message && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
              <p className="text-sm italic">"{inviteData.message}"</p>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Confirm Your Email</label>
              <p className="text-xs text-muted-foreground mb-2">
                Enter your email address to verify your identity
              </p>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder={inviteData.email}
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="pl-9"
                  data-testid="confirm-email-input"
                />
              </div>
            </div>

            {acceptMutation.error && (
              <p className="text-sm text-red-600">
                {(acceptMutation.error as Error).message}
              </p>
            )}

            <Button
              onClick={handleAccept}
              disabled={acceptMutation.isPending || !guestEmail}
              className="w-full"
              data-testid="accept-invitation-button"
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                "Accept Invitation"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
